import { contactType } from 'config/contactType';
import { entityType } from 'config/entityType';
import { ENTITY } from 'config/entityTypes';
import { invitedStatus } from 'config/invitedStatus';
import { USER_TYPE } from 'config/userType';
import {
	entryAddedNotice,
	clipNotificationText,
	collectWarnings,
	entryErrorText,
	entryFailed,
	entryIncomplete
} from 'library/helpers/peopleResults';
import { apiFailureMessage } from 'library/helpers/apiFailureMessage';
import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { authActions } from 'reduxStore/auth/slice';
import { accessActions } from 'reduxStore/access/slice';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { roleActions } from 'reduxStore/roles/slice';
import { teamsActions } from 'reduxStore/teams/slice';
import { Notification } from 'zComponents/atoms';
import { watchActivity } from '../_shared/activity';
import { selectPeople } from './selectors';
import { BULK_ADD_EMPTY_PROGRESS, peopleActions } from './slice';

// The entity a toast names: ENTITY.VISITOR for visitors, ENTITY.PERSON for everyone else.
const personEntityOf = (userTypeID) => (userTypeID === USER_TYPE.VISITOR ? ENTITY.VISITOR : ENTITY.PERSON);
// Same, for existing people: ENTITY.VISITOR only when every loaded target is a visitor.
function* resolvePersonEntity(personIDs) {
	const ids = new Set(personIDs);
	const targets = (yield select(selectPeople)).filter((p) => ids.has(p.personID));
	return targets.length > 0 && targets.every((p) => p.userTypeID === USER_TYPE.VISITOR)
		? ENTITY.VISITOR
		: ENTITY.PERSON;
}

// Silent refresh — replaces data without loading flash (used after inline edits)
export function* refreshPeople() {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const query = siteID !== 'all' ? { siteID } : {};
		const res = yield call(() => SuperFetch.get('/people', {}, query));

		if (res.statusCode === 200) {
			yield put(peopleActions.refreshPeopleSuccess({ orgID, siteID, data: res.data }));
		}
	} catch {
		// Silent refresh — don't show error notifications
	}
}

// Fetches people for the active scope (orgID + siteID from selectedProfile)
// Payload can override siteID — used by Lockers to force 'all' bucket
export function* getPerson({ payload } = {}) {
	const { orgID, siteID: profileSiteID } = yield select((state) => state.Auth.selectedProfile);
	const siteID = payload?.siteID ?? profileSiteID;
	const query = siteID !== 'all' ? { siteID } : {};

	// Set loading on target bucket (no saga listens to loadPersonStart — no loop)
	yield put(peopleActions.loadPersonStart({ orgID, siteID }));

	try {
		const people = yield call(() => SuperFetch.get('/people', {}, query));

		if (people.statusCode === 200) {
			yield put(peopleActions.loadPersonSuccess({ orgID, siteID, data: people.data }));
		} else {
			throw Error(people.error.message || people.error || 'Failed to load people.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonError({ orgID, siteID, error: error.message }));
	}
}

// Lockers — always writes to 'all' bucket (siteID: 'all')
export function* getLockerPerson() {
	const { orgID } = yield select((state) => state.Auth.selectedProfile);

	yield put(peopleActions.loadPersonStart({ orgID, siteID: 'all' }));

	try {
		const people = yield call(() => SuperFetch.get('/lockers/people'));

		if (people.statusCode === 200) {
			yield put(peopleActions.loadPersonSuccess({ orgID, siteID: 'all', data: people.data }));
		} else {
			throw Error(people.error.message || people.error || 'Failed to load people.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonError({ orgID, siteID: 'all', error: error.message }));
	}
}

// Fetch contacts for a person — patches inside the active scope bucket
export function* getPersonContacts({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { personID } = payload;

		const contacts = yield call(() => SuperFetch.get(`/people/${personID}/contacts`));

		if (contacts.statusCode === 200) {
			yield put(peopleActions.loadPersonContactSuccess({ orgID, siteID, personID, data: contacts.data }));
		} else {
			throw Error(contacts.error.message || contacts.error || 'Failed to load person contact.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonContactError(error));
	}
}

export function* addPersonContact({ payload }) {
	try {
		// Get the personID and contactInformation from the payload
		const { personID, contactInformation } = payload;

		// Upsert the contact of the selected person.
		const addPersonContact = yield call(() =>
			SuperFetch.post(`/contact`, {
				entityID: personID,
				entityTypeID: entityType.PERSON,
				contactInformation: contactInformation,
				contactTypeID: contactType.MOBILE
			})
		);

		if (addPersonContact.statusCode === 200) {
			Notification('success', 'Contact created', 'Contact created successfully.');
			yield put(peopleActions.addPersonContactSuccess({ personID }));
		} else {
			throw Error(addPersonContact.error.message || addPersonContact.error || 'Failed to add person contact.');
		}
	} catch (error) {
		yield put(peopleActions.addPersonContactError(error));
		Notification('error', 'Contact failed', error.message);
	}
}

//🔥 Actions after Add Person Contact Here.
export function* addPersonContactSuccess({ payload }) {
	const { personID } = payload;

	yield put(peopleActions.loadPersonContact({ personID }));
}

// Sends support the account deletion request email for the signed-in person; nothing is removed here.
export function* requestAccountDeletion() {
	try {
		const res = yield call(() => SuperFetch.delete('/people/permanentlyDeleteUserProfile'));
		if (res?.statusCode !== 200) throw Error(res?.error?.message || res?.error || 'Failed to send the request.');
		yield put(peopleActions.requestAccountDeletionSuccess());
		Notification('success', 'Request sent', 'Zezamii support will contact you to delete your account.');
	} catch (error) {
		yield put(peopleActions.requestAccountDeletionError(error.message));
		Notification('error', 'Request failed', error.message);
	}
}

export function* updatePersonContact({ payload }) {
	try {
		const { personID, contactID, contactInformation } = payload;

		// Update the contact of the selected person.
		const updatePersonContact = yield call(() =>
			SuperFetch.patch(`/contact/${contactID}`, {
				contactInformation: contactInformation
			})
		);

		if (updatePersonContact.statusCode === 200) {
			yield put(peopleActions.updatePersonContactSuccess({ personID }));
			Notification('success', 'Contact updated', 'Contact updated successfully.');
		} else {
			throw Error(
				updatePersonContact.error.message || updatePersonContact.error || 'Failed to update person contact.'
			);
		}
	} catch (error) {
		yield put(peopleActions.updatePersonContactError(error));
		Notification('error', 'Update failed', error.message);
	}
}

//🔥 Actions after Update Person Contact Here.
export function* updatePersonContactSuccess({ payload }) {
	const { personID } = payload;

	yield put(peopleActions.loadPersonContact({ personID }));
}

// Fetch address for a person — patches inside the active scope bucket
export function* getPersonAddress({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { personID } = payload;

		const addresses = yield call(() => SuperFetch.get(`/people/${personID}/address`));

		if (addresses.statusCode === 200) {
			yield put(peopleActions.loadPersonAddressSuccess({ orgID, siteID, personID, data: addresses.data }));
		} else {
			throw Error(addresses.error.message || addresses.error || 'Failed to load person address.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonAddressError(error));
	}
}

// Fetch bookings for a person — patches inside the active scope bucket
export function* getPersonBookings({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { personID } = payload;

		const bookings = yield call(() => SuperFetch.get(`/people/${personID}/bookings`));

		if (bookings.statusCode === 200) {
			yield put(peopleActions.loadPersonBookingSuccess({ orgID, siteID, personID, data: bookings.data }));
		} else {
			throw Error(bookings.error.message || bookings.error || 'Failed to load person bookings.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonBookingError(error));
	}
}

// Fetch teams for a person — patches inside the active scope bucket
export function* getPersonTeams({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { personID } = payload;

		const teams = yield call(() => SuperFetch.get(`/people/${personID}/teams`));

		if (teams.statusCode === 200) {
			yield put(peopleActions.loadPersonTeamsSuccess({ orgID, siteID, personID, data: teams.data }));
		} else {
			throw Error(teams.error.message || teams.error || 'Failed to load person teams.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonTeamsError(error));
	}
}

// Fetch permissions for a person — patches inside the active scope bucket
export function* getPersonPermissions({ payload }) {
	try {
		const { orgID, siteID } = yield select((state) => state.Auth.selectedProfile);
		const { personID } = payload;

		const permissions = yield call(() => SuperFetch.get(`/people/${personID}/permissions`));

		if (permissions.statusCode === 200) {
			yield put(peopleActions.loadPersonPermissionsSuccess({ orgID, siteID, personID, data: permissions.data }));
		} else {
			throw Error(permissions.error.message || permissions.error || 'Failed to load person permissions.');
		}
	} catch (error) {
		yield put(peopleActions.loadPersonPermissionsError(error));
	}
}

//🔥 Create the pending Zezamii users staged in selectedUsers (POST /people/bulk)
export function* createPeople() {
	try {
		const allUsers = yield select((state) => state.People.selectedUsers);
		const users = allUsers.filter(
			(person) => person.status === invitedStatus.PENDING && person.type === USER_TYPE.ZEZAMII_USER
		);

		if (users.length === 0) {
			yield call(Notification, 'info', 'Nothing to add', 'No pending people to add.');
			yield put(peopleActions.createPeopleError('No pending users'));
			return;
		}

		// Mark all as inviting
		for (const user of users) {
			yield put(
				peopleActions.updateSelectedUserStatus({
					personID: user.personID,
					status: invitedStatus.INVITING
				})
			);
		}

		// Build bulk payload — all users share the same siteIDs
		const siteIDs = users[0]?.siteIDs || [];
		const entries = users.map((user) => {
			// Email is optional. The app invite only goes out with a mobile credential ({ type: 5 }).
			const entry = {};
			if (user.email?.trim()) entry.email = user.email.trim();

			if (user.firstName) entry.firstName = user.firstName;
			if (user.lastName) entry.lastName = user.lastName;

			// Contact and organisation details: phone becomes a contact row, the rest person columns.
			if (user.phone) entry.phone = user.phone;
			if (user.company) entry.company = user.company;
			if (user.department) entry.department = user.department;
			if (user.division) entry.division = user.division;

			// Per-entry assignments (role, teams, credential, permissions)
			const assign = {};
			if (user.roleID) assign.roleID = user.roleID;
			if (user.teamIDs?.length) assign.teamIDs = user.teamIDs;
			// One target per rule. Group access is `assetGroupID` on BOTH endpoints — the staged rows
			// carry it already, and `deviceGroupID` is a different target (a device group).
			if (user.permissions?.length) {
				assign.permissions = user.permissions.map((p) => ({
					...(p.deviceID ? { deviceID: p.deviceID } : {}),
					...(p.assetGroupID ? { assetGroupID: p.assetGroupID } : {}),
					...(p.deviceGroupID ? { deviceGroupID: p.deviceGroupID } : {}),
					permissionTypeID: p.permissionTypeID || 1,
					...(p.accessWindowID != null ? { accessWindowID: p.accessWindowID } : {})
				}));
			}
			// IR primary permission group (IR only) — a marker, not a permission grant.
			if (user.primaryAssetGroupID != null) assign.primaryAssetGroupID = user.primaryAssetGroupID;

			// Map credentials from frontend shape to backend schema
			const credList = user.credentials || (user.credential ? [user.credential] : []);
			if (credList.length > 0) {
				const existingIDs = credList.filter((c) => c.existingCredentialID).map((c) => c.existingCredentialID);
				const newCreds = credList
					.filter((c) => !c.existingCredentialID)
					.map((c) => {
						const payload = {
							type: c.credentialTypeID,
							name: c.name || undefined,
							value: c.value || undefined
						};
						if (c.uniqueIdentifier) payload.uniqueIdentifier = c.uniqueIdentifier;
						if (c.externalCredentialTemplateID)
							payload.externalCredentialTemplateID = c.externalCredentialTemplateID;
						if (c.siteCode) payload.siteCode = c.siteCode;
						return payload;
					});
				// Credentials carry no dates — the server 400s credentialValidity; the period is entry.validity.
				if (existingIDs.length > 0) assign.credentialIDs = existingIDs;
				if (newCreds.length > 0) assign.credentials = newCreds;
			}

			if (Object.keys(assign).length > 0) entry.assign = assign;

			// Whole-person validity — carried through from the form as { validFrom, validUntil }
			// (ISO or null). Omitted entirely when not set.
			if (user.validity) entry.validity = user.validity;

			return entry;
		});

		const response = yield call(() =>
			SuperFetch.post('/people/bulk', { userTypeID: users[0]?.type, siteIDs, entries })
		);

		if (![200, 202].includes(response.statusCode)) {
			throw new Error(apiFailureMessage(response, 'Adding people failed'));
		}

		// Map results back to each user
		const results = response.data?.results || [];
		for (let i = 0; i < users.length; i++) {
			const user = users[i];
			const result = results[i];

			const label = user.email?.trim() || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Person';

			// Nothing was created or matched — the entry is still a retry.
			if (entryFailed(result)) {
				yield call(Notification, 'error', 'Add failed', `${label}: ${entryErrorText(result) || 'Add failed'}`);
				yield put(
					peopleActions.updateSelectedUserStatus({
						personID: user.personID,
						status: invitedStatus.PENDING
					})
				);
				continue;
			}

			// The person exists, so the row is done — but some of what they were given may not have
			// landed. Report that rather than claim a clean add, and never re-add them.
			const notice = entryAddedNotice(result, personEntityOf(user.type).singular);
			yield call(Notification, notice.type, notice.title, notice.text);
			yield put(
				peopleActions.updateSelectedUserStatus({
					personID: user.personID,
					status: invitedStatus.INVITED,
					newPersonID: result.personID
				})
			);
		}

		yield put(peopleActions.createPeopleSuccess());
	} catch (error) {
		yield call(
			Notification,
			'error',
			'Add failed',
			clipNotificationText(error.message || 'An unexpected error occurred.')
		);
		yield put(peopleActions.createPeopleError('Failed to add people.'));
	}
}

//🔥 After createPeople: silent refresh, plus whatever the bulk assign touched
// (teams, permissions, roles, credentials).
export function* createPeopleSuccess() {
	yield put(peopleActions.refreshPeople());

	// Reload entities that the bulk endpoint may have modified
	const lastUsers = yield select((state) => state.People.selectedUsers);
	const hadTeams = lastUsers.some((u) => u.teamIDs?.length > 0);
	const hadPermissions = lastUsers.some((u) => u.permissions?.length > 0);
	const hadCredential = lastUsers.some((u) => u.credential || u.credentials?.length > 0);
	const hadRole = lastUsers.some((u) => u.roleID);

	if (hadTeams) {
		yield put(teamsActions.refreshTeams());
		// Refresh members for each affected team
		const teamIDs = [...new Set(lastUsers.flatMap((u) => u.teamIDs || []))];
		for (const teamID of teamIDs) {
			yield put(teamsActions.refreshTeamMembers({ teamID }));
		}
	}
	if (hadPermissions) yield put(accessActions.refreshAccessPermissions());
	// The Roles page caches for a minute; a role assigned here must show there on the next visit.
	if (hadRole) yield put(roleActions.loadRoles());
	// A credential created here is already on its way to the locks. v5 signals propagation with
	// `credential:propagation:changed`, so take the snapshot now rather than await the first signal.
	if (hadCredential) {
		yield put(credentialsActions.refreshCredentials());
		yield put(credentialsActions.fetchPropagation());
	}
}

// The bulk endpoint takes at most 50 entries per request.
const BULK_ADD_CHUNK = 50;

const bulkAddEntry = (row) => {
	const entry = { firstName: row.firstName.trim(), lastName: row.lastName.trim() };
	if (row.email?.trim()) entry.email = row.email.trim();
	return entry;
};

// Report against the person, not the position in the chunk.
const bulkAddLabel = (row = {}) => ({
	email: row.email?.trim() || null,
	name: [row.firstName, row.lastName].filter(Boolean).join(' ').trim() || null
});

const bulkAddSummary = ({ created, failed, aborted }) =>
	[created > 0 && `${created} created`, failed > 0 && `${failed} failed`, aborted && 'cancelled before the rest']
		.filter(Boolean)
		.join(', ');

//🔥 Bulk Add People — chunked POST /people/bulk; orgID is passed explicitly so the path never
// carries `undefined`. Progress lives in People.bulkAdd, so closing the drawer loses nothing.
export function* bulkAddPeople({ payload } = {}) {
	const { rows = [], siteID, userTypeID } = payload || {};
	const totals = { ...BULK_ADD_EMPTY_PROGRESS, total: rows.length, failedRows: [], warnings: [] };
	const failRows = (chunkRows, reason) => {
		totals.failed += chunkRows.length;
		chunkRows.forEach((row) => totals.failedRows.push({ ...bulkAddLabel(row), reason }));
	};

	const orgID = yield select((state) => state.Auth?.selectedProfile?.orgID);
	if (!orgID) {
		failRows(rows, 'No organisation selected');
		totals.completed = rows.length;
		yield put(peopleActions.bulkAddPeopleDone(totals));
		yield call(Notification, 'error', 'People not created', 'No organisation selected. Reload and try again.');
		return;
	}

	for (let i = 0; i < rows.length; i += BULK_ADD_CHUNK) {
		const cancelRequested = yield select((state) => state.People.bulkAdd.cancelRequested);
		if (cancelRequested) {
			totals.aborted = true;
			break;
		}

		const chunkRows = rows.slice(i, i + BULK_ADD_CHUNK);
		try {
			const response = yield call(
				SuperFetch.post,
				'/people/bulk',
				{ userTypeID, siteIDs: [siteID], entries: chunkRows.map(bulkAddEntry) },
				{},
				orgID
			);
			if ([200, 201, 202].includes(response?.statusCode)) {
				(response.data?.results || []).forEach((result, j) => {
					const label = bulkAddLabel(chunkRows[j]);
					// Create-only: an email already in the org comes back failed, with the server's reason.
					if (result.status === 'created') totals.created++;
					else {
						totals.failed++;
						totals.failedRows.push({ ...label, reason: result.errors?.join('; ') || 'Unknown error' });
					}
					(result.warnings || []).forEach((message) => totals.warnings.push({ ...label, message }));
				});
			} else {
				failRows(chunkRows, apiFailureMessage(response, 'Server error'));
			}
		} catch (error) {
			failRows(chunkRows, error.message || 'Network error');
		}

		totals.completed += chunkRows.length;
		yield put(
			peopleActions.bulkAddPeopleProgress({
				...totals,
				failedRows: [...totals.failedRows],
				warnings: [...totals.warnings]
			})
		);
	}

	yield put(peopleActions.bulkAddPeopleDone(totals));
	if (totals.created > 0) yield put(peopleActions.refreshPeople());

	const nothingLanded = totals.created === 0;
	yield call(
		Notification,
		nothingLanded ? 'error' : totals.failed > 0 || totals.aborted ? 'warning' : 'success',
		nothingLanded ? 'People not created' : 'People added',
		`${bulkAddSummary(totals)}.`
	);
}

//🔥 Create the pending visitors staged in selectedUsers
export function* createVisitors() {
	try {
		const allUsers = yield select((state) => state.People.selectedUsers);

		const users = allUsers.filter(
			(person) => person.status === invitedStatus.PENDING && person.type === USER_TYPE.VISITOR
		);

		if (users.length === 0) {
			yield call(Notification, 'info', 'Nothing to add', 'No pending visitors to add.');
			return;
		}

		for (const user of users) {
			try {
				yield put(
					peopleActions.updateSelectedUserStatus({
						personID: user.personID,
						status: invitedStatus.INVITING
					})
				);

				// Build per-entry assign object from teams + permissions + credentials
				const assign = {};

				if (user.teamIDs?.length) assign.teamIDs = user.teamIDs;

				// IR primary permission group (IR only) — a marker, not a permission grant.
				if (user.primaryAssetGroupID != null) assign.primaryAssetGroupID = user.primaryAssetGroupID;

				if (user.permissions?.length) {
					assign.permissions = user.permissions.map((p) => ({
						...(p.deviceID ? { deviceID: p.deviceID } : {}),
						...(p.assetGroupID ? { assetGroupID: p.assetGroupID } : {}),
						permissionTypeID: p.permissionTypeID || 1,
						accessWindowID: p.accessWindowID ?? null
					}));
				}

				if (user.credentials?.length) {
					const existingIDs = user.credentials
						.filter((c) => c.existingCredentialID)
						.map((c) => c.existingCredentialID);
					const newCreds = user.credentials
						.filter((c) => !c.existingCredentialID)
						.map((c) => {
							const payload = {
								type: c.credentialTypeID,
								name: c.name || undefined,
								value: c.value || undefined
							};
							if (c.uniqueIdentifier) payload.uniqueIdentifier = c.uniqueIdentifier;
							if (c.externalCredentialTemplateID)
								payload.externalCredentialTemplateID = c.externalCredentialTemplateID;
							if (c.siteCode) payload.siteCode = c.siteCode;
							return payload;
						});
					// A credential carries no time: the visitor's access period is the person's `validity`.
					if (existingIDs.length > 0) assign.credentialIDs = existingIDs;
					if (newCreds.length > 0) assign.credentials = newCreds;
				}

				const entry = {
					firstName: user.firstName,
					lastName: user.lastName,
					...(user.email?.trim() ? { email: user.email.trim() } : {}),
					// Phone becomes a contact row; company/department/division are person columns.
					...(user.phone ? { phone: user.phone } : {}),
					...(user.company ? { company: user.company } : {}),
					...(user.department ? { department: user.department } : {}),
					...(user.division ? { division: user.division } : {}),
					...(Object.keys(assign).length > 0 ? { assign } : {}),
					// Whole-person validity — { validFrom, validUntil } (ISO or null). Required of a visitor:
					// the App Server refuses one without validUntil.
					...(user.validity ? { validity: user.validity } : {})
				};

				const response = yield call(() =>
					SuperFetch.post('/people/bulk', {
						userTypeID: user.type,
						siteIDs: user.siteIDs?.length ? user.siteIDs : [],
						entries: [entry]
					})
				);

				if (![200, 201, 202].includes(response.statusCode)) {
					throw new Error(apiFailureMessage(response, 'Failed to add user.'));
				}

				const result = response.data?.results?.[0];
				if (entryFailed(result)) {
					throw new Error(entryErrorText(result) || 'Failed to add user.');
				}

				// A 2xx is a report: the person exists, but a credential or grant inside the entry
				// may still have been refused, and warnings only ever surface here.
				const notice = entryAddedNotice(result, personEntityOf(user.type).singular);
				yield call(Notification, notice.type, notice.title, notice.text);

				yield put(
					peopleActions.updateSelectedUserStatus({
						personID: user.personID,
						status: invitedStatus.INVITED,
						newPersonID: result.personID
					})
				);
			} catch (err) {
				yield call(
					Notification,
					'error',
					'Add failed',
					clipNotificationText(err.message || 'Failed to add user.')
				);
				yield put(
					peopleActions.updateSelectedUserStatus({
						personID: user.personID,
						status: invitedStatus.PENDING
					})
				);
			}
		}

		yield put(peopleActions.createVisitorsSuccess());
	} catch (error) {
		yield call(
			Notification,
			'error',
			'Add failed',
			clipNotificationText(error.message || 'An unexpected error occurred.')
		);
		yield put(peopleActions.createVisitorsError('Failed to add visitors.'));
	}
}

//🔥 Actions after creating visitors.
export function* createVisitorsSuccess() {
	yield put(peopleActions.refreshPeople());
	yield put(teamsActions.refreshTeams());
	yield put(accessActions.refreshAccessPermissions());
	yield put(credentialsActions.refreshCredentials());
	// Same as the Zezamii path: a card issued here is already on its way to the locks.
	yield put(credentialsActions.fetchPropagation());
}

export function* updatePerson(action) {
	const { personID, data } = action.payload;

	try {
		// Get the currently selected profile's personID (used to know if the user is updating their own profile)
		const { personID: currentPersonProfileID } = yield select((state) => state.Auth.selectedProfile);
		const entity = yield call(resolvePersonEntity, [personID]);

		// Send PATCH request to update person data
		const response = yield call(() => SuperFetch.patch(`/people/${personID}`, data));

		// If the response is not successful, throw an error to trigger the catch block
		if (response.statusCode !== 200) {
			throw Error(apiFailureMessage(response, 'Failed to update person profile.'));
		}

		// Dispatch success action to update state
		yield put(peopleActions.updatePersonSuccess());

		// Show success notification to the user
		Notification('success', `${entity.singular} updated`, `${entity.singular} updated successfully.`);

		// If the updated profile is the same as the currently logged-in user,
		// re-fetch their profile to refresh the UI with updated data
		if (currentPersonProfileID === personID) {
			yield put(authActions.getUserProfile());
		}
	} catch (error) {
		yield put(peopleActions.updatePersonError(error));

		// Show error notification to the user
		Notification('error', 'Update failed', clipNotificationText(error.message));
	}
}

//🔥 Actions after Update Person Here.
// Silent refresh — no loading flash (same pattern as credentials refreshCredentials)
export function* updatePersonSuccess() {
	yield put(peopleActions.refreshPeople());
}

//✅ Person Sites
export function* updatePersonSites({ payload }) {
	try {
		const { personID, siteIDs } = payload;
		const result = yield call(() => SuperFetch.put(`/people/${personID}/sites`, { siteIDs }));
		if (result.statusCode === 200) {
			yield put(peopleActions.updatePersonSitesSuccess());
			Notification('success', 'Sites updated', 'Person sites updated successfully.');
			yield put(peopleActions.refreshPeople());
		} else {
			throw Error(apiFailureMessage(result, 'Failed to update person sites.'));
		}
	} catch (error) {
		Notification('error', 'Update failed', clipNotificationText(error.message || 'Failed to update person sites.'));
		yield put(peopleActions.updatePersonSitesError(error));
	}
}

export function* resetPersonPassword({ payload }) {
	const { email } = payload;

	try {
		// Public route (unAuthenticateMisc). platform 'Access' makes the email say "Zezamii Access";
		// 'Dashboard' mislabelled it "Zezamii Platform".
		const res = yield call(() => SuperFetch.post(`/resetPassword`, { email, platform: 'Access' }, {}, 1));
		if (res.statusCode === 200) {
			yield put(peopleActions.resetPersonPasswordSuccess());
			Notification('success', 'Password reset', 'Password reset email sent successfully.');
		} else {
			throw Error(apiFailureMessage(res, 'Failed to send password reset.'));
		}
	} catch (error) {
		yield put(peopleActions.resetPersonPasswordError(error));
		Notification('error', 'Reset failed', error.message);
	}
}

// 🛑 The AUTHENTICATED reset, distinct from the public one above. That route takes an address from
// anyone, so it can only point at a reset; this one mints a temporary password and emails it.
export function* resetPersonPasswordByAdmin({ payload }) {
	const { personID } = payload;

	try {
		// SuperFetch pins the active orgID into the path, so the route resolves to
		// /v5/{orgID}/people/{personID}/reset-password.
		const res = yield call(() => SuperFetch.post(`/people/${personID}/reset-password`, {}));
		if (res.statusCode === 200) {
			yield put(peopleActions.resetPersonPasswordByAdminSuccess());
			Notification('success', 'Password reset', 'Temporary password emailed.');
		} else {
			throw Error(apiFailureMessage(res, 'Failed to reset this password.'));
		}
	} catch (error) {
		yield put(peopleActions.resetPersonPasswordByAdminError(error));
		Notification('error', 'Reset failed', error.message);
	}
}

export function* enablePerson(action) {
	const { personIDs } = action.payload;

	try {
		// 200 + `changed: false` = already enabled; nothing propagates to the locks.
		const results = yield all(
			personIDs.map((personID) =>
				call(function* () {
					const res = yield call(() => SuperFetch.patch(`/people/${personID}/enablePerson`));

					if (res.statusCode !== 200) {
						throw Error(apiFailureMessage(res, `Failed to enable. Please try again.`));
					}
					return res.data?.changed !== false;
				})
			)
		);

		yield put(peopleActions.enablePersonSuccess());

		const entity = yield call(resolvePersonEntity, personIDs);
		const changed = results.filter(Boolean).length;
		const plural = personIDs.length > 1;
		if (changed === 0) {
			Notification(
				'info',
				`Already enabled`,
				`${plural ? 'These profiles were' : 'This profile was'} already enabled.`
			);
		} else {
			Notification(
				'success',
				plural
					? `${changed} ${entity.label.toLowerCase()} enabled successfully.`
					: `${entity.singular} enabled successfully.`
			);
		}
	} catch (error) {
		yield put(peopleActions.enablePersonError(error));
		Notification('error', 'Enable failed', error.message);
	}
}

//🔥 Actions after Enable Person Here.
export function* enablePersonSuccess() {
	yield put(peopleActions.refreshPeople());
}

export function* disablePerson(action) {
	const { personIDs } = action.payload;

	try {
		// 200 + `changed: false` = already disabled; nothing propagates to the locks.
		const results = yield all(
			personIDs.map((personID) =>
				call(function* () {
					const res = yield call(() => SuperFetch.patch(`/people/${personID}/disablePerson`));

					if (res.statusCode !== 200) {
						throw Error(apiFailureMessage(res, `Failed to disable. Please try again.`));
					}
					return res.data?.changed !== false;
				})
			)
		);

		yield put(peopleActions.disablePersonSuccess());

		const entity = yield call(resolvePersonEntity, personIDs);
		const changed = results.filter(Boolean).length;
		const plural = personIDs.length > 1;
		if (changed === 0) {
			Notification(
				'info',
				`Already disabled`,
				`${plural ? 'These profiles were' : 'This profile was'} already disabled.`
			);
		} else {
			Notification(
				'success',
				plural
					? `${changed} ${entity.label.toLowerCase()} disabled successfully.`
					: `${entity.singular} disabled successfully.`
			);
		}
	} catch (error) {
		yield put(peopleActions.disablePersonError(error));
		Notification('error', 'Disable failed', error.message);
	}
}

//🔥 Actions after Disable Person Here.
export function* disablePersonSuccess() {
	yield put(peopleActions.refreshPeople());
}

// ✅ Delete one or many people via the bulk endpoint. Each delete is independent, so the response
// carries per-person results plus a { deleted, failed } summary.
export function* deletePerson(action) {
	const { personIDs } = action.payload;

	try {
		// Bulk endpoint accepts at most 50 IDs per request
		const chunks = [];
		for (let i = 0; i < personIDs.length; i += 50) chunks.push(personIDs.slice(i, i + 50));
		// Resolved before the delete — the people leave the store once it lands.
		const entity = yield call(resolvePersonEntity, personIDs);

		const responses = yield all(
			chunks.map((chunk) => call(() => SuperFetch.delete('/people/bulk', { personIDs: chunk })))
		);

		let deleted = 0;
		let failed = 0;
		// Capture the backend's per-person error so the user sees WHY a delete failed,
		// not just a count (results[].errors[0] is the real message, e.g. an FK conflict).
		const failureReasons = [];
		responses.forEach((res) => {
			if (![200, 204].includes(res.statusCode)) {
				throw Error(apiFailureMessage(res, 'Failed to delete. Please try again.'));
			}
			const summary = res.data?.summary || res.summary || {};
			deleted += summary.deleted || 0;
			failed += summary.failed || 0;
			(res.data?.results || []).forEach((r) => {
				if (r?.status === 'failed' && r.errors?.length) failureReasons.push(r.errors[0]);
			});
		});

		if (deleted === 0 && failed > 0) {
			const reason = failureReasons[0] ? ` ${clipNotificationText(failureReasons[0])}` : '';
			throw Error(
				`Failed to delete ${failed} ${failed > 1 ? entity.label.toLowerCase() : entity.singular.toLowerCase()}.${reason}`
			);
		}

		yield put(peopleActions.deletePersonSuccess());

		if (failed > 0) {
			Notification(
				'warning',
				`Some ${entity.label.toLowerCase()} were not deleted`,
				`${deleted} deleted, ${failed} failed.`
			);
		} else {
			Notification(
				'success',
				deleted > 1 ? `${entity.label} deleted` : `${entity.singular} deleted`,
				deleted > 1
					? `${deleted} ${entity.label.toLowerCase()} deleted successfully.`
					: `${entity.singular} deleted successfully.`
			);
		}
	} catch (error) {
		yield put(peopleActions.deletePersonError(error));
		Notification('error', 'Delete failed', clipNotificationText(error.message, 140));
	}
}

//🔥 Actions after Delete Person Here.
export function* deletePersonSuccess() {
	yield put(peopleActions.refreshPeople());
	// Deleted person may have had permissions/credentials/team memberships/role assignments
	yield put(accessActions.refreshAccessPermissions());
	yield put(credentialsActions.refreshCredentials());
	// A deleted visitor's cloud keys change status server-side.
	yield put(credentialsActions.refreshCloudKeys());
	yield put(teamsActions.refreshTeams());
	yield put(roleActions.loadRoles());
}

// ── Bulk Grant Access (Quick Action) ────────────────────────────────────────
// Existing people only, chunked at 50; collects per-person results + propagation credentialIDs.
const GRANT_ACCESS_CHUNK = 50;

export function* manageAccessBulk({ payload }) {
	try {
		const { entries = [] } = payload || {};
		if (!entries.length) {
			yield put(peopleActions.manageAccessBulkError('No entries to grant.'));
			return;
		}

		// /people/access takes only { orgID, entries }: no siteIDs (resolved from Device.Location) and no
		// userTypeID (it never creates). orgID is in the URL too, but the schema validates the body.
		const { orgID } = yield select((state) => state.Auth.selectedProfile);

		const chunks = [];
		for (let i = 0; i < entries.length; i += GRANT_ACCESS_CHUNK) {
			chunks.push(entries.slice(i, i + GRANT_ACCESS_CHUNK));
		}

		const results = [];
		const propagationCredentialIDs = new Set();
		let propagationStatus = 'none';
		let socketEvent = null;

		for (const chunk of chunks) {
			const body = { orgID, entries: chunk };
			// PATCH /people/access edits access (permissions, teams, cards, IR primary group). POST
			// /people/bulk is the create/enrol contract — don't use it here.
			const res = yield call(() => SuperFetch.patch('/people/access', body));

			if (![200, 201, 202].includes(res.statusCode)) {
				const reason = apiFailureMessage(res, 'Server error');
				chunk.forEach((e) =>
					results.push({ personID: e.personID, status: 'failed', errors: [reason], warnings: [] })
				);
				continue;
			}

			const data = res.data || {};
			(data.results || []).forEach((r) => results.push(r));

			const prop = data.propagation;
			if (prop?.credentialIDs?.length) {
				prop.credentialIDs.forEach((id) => propagationCredentialIDs.add(id));
				if (prop.status && prop.status !== 'none') propagationStatus = prop.status;
				socketEvent = prop.socketEvent || socketEvent;
			}
		}

		const propagation = propagationCredentialIDs.size
			? { status: propagationStatus, credentialIDs: [...propagationCredentialIDs], socketEvent }
			: null;

		yield put(peopleActions.manageAccessBulkSuccess({ results, propagation }));

		// Refresh downstream data so the tables + propagation indicator reflect the grants/cards.
		yield put(peopleActions.refreshPeople());
		yield put(accessActions.refreshAccessPermissions());
		yield put(credentialsActions.refreshCredentials());
		yield put(credentialsActions.fetchPropagation());

		// The toast reads the entries, not the status: `partial` is neither a clean success nor a
		// failure, and warnings[] is the only place a reassignment or a skipped revoke is mentioned.
		const failed = results.filter((r) => entryFailed(r)).length;
		const partial = results.filter((r) => !entryFailed(r) && entryIncomplete(r)).length;
		const warnings = collectWarnings(results);
		const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;

		if (failed === 0 && partial === 0) {
			Notification(
				'success',
				'Access updated',
				[`Access updated for ${people(results.length)}.`, ...warnings].join(' ')
			);
		} else {
			// What went wrong, deduped — the same refusal for several people shows once.
			const problems = [
				...new Set(
					results
						.filter((r) => entryIncomplete(r))
						.map((r) => entryErrorText(r))
						.filter(Boolean)
				)
			];
			const counts = [
				results.length - failed - partial > 0 && `${people(results.length - failed - partial)} updated`,
				partial > 0 && `${partial} partly`,
				failed > 0 && `${failed} failed`
			].filter(Boolean);

			Notification(
				failed === results.length ? 'error' : 'warning',
				failed === results.length ? 'Access not updated' : 'Partially updated',
				[`${counts.join(', ')}.`, ...problems.slice(0, 3), ...warnings].join(' ')
			);
		}
	} catch (error) {
		Notification('error', 'Grant failed', error.message || 'Failed to grant access.');
		yield put(peopleActions.manageAccessBulkError(error.message || 'Failed to grant access.'));
	}
}

// Mutations that change a person outside the people endpoints. Refetching keeps lastModifiedAt
// ("Modified") and the person's derived fields current in open drawers.
const PERSON_CHANGE_ACTIONS = [
	credentialsActions.createCredentialSuccess.type,
	credentialsActions.updateCredentialSuccess.type,
	credentialsActions.deleteCredentialSuccess.type,
	credentialsActions.deleteCredentialsSuccess.type,
	credentialsActions.assignCredentialSuccess.type,
	credentialsActions.unassignCredentialSuccess.type,
	credentialsActions.issueCloudKeySuccess.type,
	credentialsActions.resendCloudKeySuccess.type,
	credentialsActions.revokeCloudKeySuccess.type,
	teamsActions.addTeamMemberSuccess.type,
	teamsActions.deleteTeamMemberSuccess.type
];

export function* refreshPeopleOnPersonChange() {
	yield put(peopleActions.refreshPeople());
}

export default function* rootSaga() {
	yield takeLatest(PERSON_CHANGE_ACTIONS, refreshPeopleOnPersonChange);
	yield takeLatest(peopleActions.loadPerson, getPerson);
	yield takeLatest(peopleActions.refreshPeople, refreshPeople);
	yield takeLatest(peopleActions.loadPersonTeams, getPersonTeams);
	yield takeLatest(peopleActions.loadPersonContact, getPersonContacts);
	yield takeLatest(peopleActions.addPersonContact, addPersonContact);
	yield takeLatest(peopleActions.addPersonContactSuccess, addPersonContactSuccess);
	yield takeLatest(peopleActions.updatePersonContact, updatePersonContact);
	yield takeLatest(peopleActions.requestAccountDeletion, requestAccountDeletion);
	yield takeLatest(peopleActions.updatePersonContactSuccess, updatePersonContactSuccess);
	yield takeLatest(peopleActions.loadPersonAddress, getPersonAddress);
	yield takeLatest(peopleActions.loadPersonBooking, getPersonBookings);
	yield takeLatest(peopleActions.loadPersonPermissions, getPersonPermissions);
	yield takeLatest(peopleActions.createPeople, createPeople);
	yield takeLatest(peopleActions.manageAccessBulk, manageAccessBulk);
	yield takeLatest(peopleActions.createPeopleSuccess, createPeopleSuccess);
	yield takeLeading(peopleActions.bulkAddPeople, bulkAddPeople);
	yield takeLatest(peopleActions.createVisitors, createVisitors);
	yield takeLatest(peopleActions.createVisitorsSuccess, createVisitorsSuccess);
	yield takeLatest(peopleActions.updatePerson, updatePerson);
	yield takeLatest(peopleActions.updatePersonSuccess, updatePersonSuccess);
	yield takeLatest(peopleActions.updatePersonSites, updatePersonSites);
	yield takeLatest(peopleActions.resetPersonPassword, resetPersonPassword);
	yield takeLatest(peopleActions.resetPersonPasswordByAdmin, resetPersonPasswordByAdmin);
	yield takeLatest(peopleActions.enablePerson, enablePerson);
	yield takeLatest(peopleActions.enablePersonSuccess, enablePersonSuccess);
	yield takeLatest(peopleActions.disablePerson, disablePerson);
	yield takeLatest(peopleActions.disablePersonSuccess, disablePersonSuccess);
	yield takeLatest(peopleActions.deletePerson, deletePerson);
	yield takeLatest(peopleActions.deletePersonSuccess, deletePersonSuccess);
	yield* watchActivity(peopleActions);
}
